import { useCallback, useEffect, useMemo, useRef, useState } from "react";

const STORAGE_PREFIX = "inventrium:form-draft";

const isBrowser = typeof window !== "undefined";

const safeParse = (value, fallback) => {
  try {
    return JSON.parse(value);
  } catch {
    return fallback;
  }
};

const isObject = (value) =>
  value !== null && typeof value === "object" && !Array.isArray(value);

const pick = (obj, keys) => {
  if (!Array.isArray(keys) || !keys.length) return { ...obj };

  return keys.reduce((acc, key) => {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      acc[key] = obj[key];
    }
    return acc;
  }, {});
};

const buildStorageKey = ({ scope = "global", formId, version = "v1" }) => {
  if (!formId) {
    throw new Error("usePersistentForm requires a formId.");
  }

  return `${STORAGE_PREFIX}:${scope}:${formId}:${version}`;
};

export function usePersistentForm({
  formId,
  scope,
  version = "v1",
  initialValues,
  persistFields,
  ttlMs = 1000 * 60 * 60 * 24 * 30, // 30 days
  debounceMs = 250,
}) {
  const storageKey = useMemo(
    () => buildStorageKey({ scope, formId, version }),
    [scope, formId, version],
  );

  const getInitialState = useCallback(() => {
    const baseState =
      typeof initialValues === "function" ? initialValues() : initialValues;

    if (!isBrowser) return baseState;

    const raw = window.localStorage.getItem(storageKey);
    if (!raw) return baseState;

    const parsed = safeParse(raw, null);

    if (!parsed || !isObject(parsed)) return baseState;

    const { values, expiresAt } = parsed;

    if (expiresAt && Date.now() > expiresAt) {
      window.localStorage.removeItem(storageKey);
      return baseState;
    }

    if (!isObject(values)) return baseState;

    return {
      ...baseState,
      ...values,
    };
  }, [initialValues, storageKey]);

  const [form, setForm] = useState(getInitialState);
  const saveTimeoutRef = useRef(null);
  const hydratedRef = useRef(false);

  useEffect(() => {
    hydratedRef.current = true;
  }, []);

  const persistNow = useCallback(
    (nextForm) => {
      if (!isBrowser) return;

      const valuesToPersist = pick(nextForm, persistFields);

      const payload = {
        values: valuesToPersist,
        savedAt: Date.now(),
        expiresAt: ttlMs ? Date.now() + ttlMs : null,
      };

      window.localStorage.setItem(storageKey, JSON.stringify(payload));
    },
    [persistFields, storageKey, ttlMs],
  );

  useEffect(() => {
    if (!hydratedRef.current) return;

    if (saveTimeoutRef.current) {
      window.clearTimeout(saveTimeoutRef.current);
    }

    saveTimeoutRef.current = window.setTimeout(() => {
      persistNow(form);
    }, debounceMs);

    return () => {
      if (saveTimeoutRef.current) {
        window.clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [form, debounceMs, persistNow]);

  const updateField = useCallback((key, value) => {
    setForm((prev) => ({
      ...prev,
      [key]: value,
    }));
  }, []);

  const updateFields = useCallback((updates) => {
    setForm((prev) => ({
      ...prev,
      ...updates,
    }));
  }, []);

  const resetForm = useCallback(() => {
    const baseState =
      typeof initialValues === "function" ? initialValues() : initialValues;

    setForm(baseState);

    if (isBrowser) {
      window.localStorage.removeItem(storageKey);
    }
  }, [initialValues, storageKey]);



  return {
    form,
    setForm,
    updateField,
    updateFields,
    resetForm,
    persistNow,
    storageKey,
  };
}